import React from 'react'
import { Maybe, ProductInterface } from '@pylot-data/fwrdschema'
import { getOfferData, getAggregateRating, getReviews } from './functions'
import { getStringAttributeValue, getTrimmedUrl } from '../../util'
import { JsonLdScript } from '../../JsonLdScript/JsonLdScript'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'

type ProductJSONProps = {
    product: ProductInterface
    title: string
    description: string
    images?: Maybe<Array<{ url: string }>>
}

export const ProductJsonLd = ({
    product,
    title,
    description
}: ProductJSONProps): JSX.Element => {
    const config = useStoreConfig()
    const {
        base: {
            url: {
                baseUrl,
                path: { shippingSettings }
            },
            shipping: { addressCountry, includeShippingDetails }
        }
    } = config
    const shippingSettingsLink = `${baseUrl}${shippingSettings}`
    const { variant } = useProductUI(product)

    const sku = variant?.sku

    const getShippingDetails = includeShippingDetails
        ? () => ({
              shippingSettingsLink,
              '@type': 'OfferShippingDetails',
              name: 'International',
              doesNotShip: true,
              shippingDestination: {
                  '@type': 'DefinedRegion',
                  addressCountry
              }
          })
        : undefined

    const defaultBrand = config.seo.product?.defaultBrand
        ? String(config.seo.product?.defaultBrand)
        : undefined
    const brand = getStringAttributeValue(
        product,
        'brand',
        config,
        defaultBrand
    )
    const brandData = brand ? { brand: { '@type': 'Thing', name: brand } } : {}

    const size = getStringAttributeValue(product, 'size', config)
    const sizeData = size ? { size } : {}

    const getImages = () => {
        const imageArray: Array<string> = []

        product.media_gallery?.map((value) => {
            imageArray.push(getTrimmedUrl(value?.url!))
        })
        return imageArray
    }
    const jsonData = {
        '@context': 'http://schema.org/',
        '@type': 'Product',
        name: product?.name,
        sku: sku,
        mpn: sku,
        image: getImages(),
        description,
        ...sizeData,
        ...brandData,
        ...getOfferData(
            product,
            title,
            description,
            config,
            getShippingDetails
        ),

        ...getAggregateRating(product, config),
        ...getReviews(product)
    }

    return (
        <JsonLdScript
            id={`product-json-ld-${sku ?? product?.sku ?? 'unknown'}`}
            data={jsonData}
        />
    )
}
